import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import {
  api,
  extractApiError,
  isPlanLimitError,
  isSessionEndedError,
} from '../lib/api'
import {
  approveSopResponse,
  generateSopResponse,
  sopBlueprintResponse,
} from '../lib/types'
import { CONTAINS_LETTER } from '../lib/validators'

// Subtask nesting cap. 0 = top-level task; 1 = subtask under a task;
// 2 = sub-subtask under a subtask. Matches the backend's
// `MAX_SUBTASK_DEPTH` in `sops.service.js` so the preview can never
// produce a tree deeper than the seeder accepts.
const MAX_DEPTH = 2

// ── Immutable tree helpers ────────────────────────────────────────
// All three return a NEW root array — the React state setter just
// swaps the reference, so React never observes a mutation in place.

/** Apply `patch` to the node addressed by `path` (array of indices). */
function updateAtPath(tree, path, patch) {
  if (path.length === 0) return tree
  const [head, ...rest] = path
  return tree.map((node, i) => {
    if (i !== head) return node
    if (rest.length === 0) return { ...node, ...patch }
    return { ...node, subtasks: updateAtPath(node.subtasks ?? [], rest, patch) }
  })
}

/** Remove the node addressed by `path`. */
function removeAtPath(tree, path) {
  if (path.length === 0) return tree
  const [head, ...rest] = path
  if (rest.length === 0) return tree.filter((_, i) => i !== head)
  return tree.map((node, i) => {
    if (i !== head) return node
    return { ...node, subtasks: removeAtPath(node.subtasks ?? [], rest) }
  })
}

/** Append a blank child to the node addressed by `path`. An empty
 *  path appends a new top-level task. */
function addChildAtPath(tree, path, isTask) {
  const blank = isTask
    ? { name: 'New task', description: '', subtasks: [] }
    : { name: 'New subtask', description: '', subtasks: [] }
  if (path.length === 0) return [...tree, blank]
  const [head, ...rest] = path
  return tree.map((node, i) => {
    if (i !== head) return node
    if (rest.length === 0) {
      return { ...node, subtasks: [...(node.subtasks ?? []), blank] }
    }
    return { ...node, subtasks: addChildAtPath(node.subtasks ?? [], rest, false) }
  })
}

/** Count every descendant (subtasks at every depth). Used for the
 *  "X tasks and Y subtasks" header summary. */
function countDescendants(tree) {
  if (!Array.isArray(tree)) return 0
  return tree.reduce(
    (n, t) => n + (t.subtasks?.length ?? 0) + countDescendants(t.subtasks ?? []),
    0,
  )
}

/** Recursively trim + drop empty names from a tree before posting
 *  the approval. Always emits `subtasks: []` (never undefined) so the
 *  backend's recursive cleaner never has to defend against missing
 *  keys at any depth. */
function cleanTree(nodes) {
  if (!Array.isArray(nodes)) return []
  return nodes
    .map((n) => {
      const node = {
        name: String(n.name ?? '').trim(),
        description: String(n.description ?? '').trim(),
        subtasks: cleanTree(n.subtasks ?? []),
      }
      // Slice 3c — preserve the AI's priority + effort estimate through
      // edits so they reach the created tasks (the editor doesn't touch them).
      if (n.priority) node.priority = n.priority
      if (n.estimatedMinutes) node.estimatedMinutes = n.estimatedMinutes
      return node
    })
    .filter((n) => n.name.length >= 2)
}

/** First task/subtask name validation error in the tree, or null if all names
 *  are valid. Blocks Create Tasks with a clear message rather than silently
 *  dropping the node in `cleanTree` (→ empty payload → generic server error).
 *  Reuses the shared `CONTAINS_LETTER` rule so a symbols-only name like
 *  "#%%%%" is rejected, matching List / Project / Space name validation. */
function firstNameError(nodes) {
  if (!Array.isArray(nodes)) return null
  for (const n of nodes) {
    const name = String(n.name ?? '').trim()
    if (name.length === 0) return 'Task Name is required.'
    // 100 matches the tasks DTO. The library-SOP flow creates each node via the
    // per-task endpoint, so blocking here avoids a partial create (some tasks
    // made, then a mid-loop failure) on an over-long name from an older draft.
    if (name.length > 100) return 'Task Name must be at most 100 characters.'
    if (!CONTAINS_LETTER.test(name))
      return 'Task Name must contain at least one letter.'
    const childError = firstNameError(n.subtasks ?? [])
    if (childError) return childError
  }
  return null
}

// ── Recursive renderer ────────────────────────────────────────────

/** Renders one node + its children, recursively. `depth` controls
 *  indent + whether to show a description textarea (top-level only)
 *  + whether the "Add subtask" affordance shows (only when nesting
 *  another level would still be under `MAX_DEPTH`). */
function NodeEditor({ node, path, depth, onUpdate, onRemove, onAddChild }) {
  const isTask = depth === 0
  // Indent steps. Top-level (task) sits at the dialog edge; subtasks
  // and sub-subtasks each indent further with a colored gutter so the
  // hierarchy is unmistakable.
  const wrapperClass =
    depth === 0
      ? 'rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-3 space-y-2'
      : depth === 1
        ? 'pl-3 border-l-2 border-[color:var(--color-rule-strong,_#e2e8f0)] space-y-1.5'
        : 'pl-3 ml-3 border-l-2 border-[color:var(--color-rule)] space-y-1.5'
  return (
    <div className={wrapperClass}>
      {isTask ? (
        // Top-level: input + remove + description textarea
        <>
          <div className="flex items-start gap-2">
            <input
              value={node.name}
              onChange={(e) => onUpdate(path, { name: e.target.value })}
              maxLength={100}
              placeholder="Task name"
              className="flex-1 min-w-0 px-2 py-1.5 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm font-medium text-[color:var(--color-ink-strong)] outline-none focus:border-[color:var(--color-accent)]"
            />
            <button
              type="button"
              onClick={() => onRemove(path)}
              title="Remove task"
              aria-label="Remove task"
              className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <textarea
            value={node.description ?? ''}
            onChange={(e) => onUpdate(path, { description: e.target.value })}
            rows={2}
            maxLength={5000}
            placeholder="Description (optional)"
            className="w-full px-2 py-1.5 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-xs text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] resize-y"
          />
          {/* AI-suggested priority + effort estimate are intentionally NOT
           *  shown here to keep the review focused on the task breakdown. The
           *  suggested priority is still APPLIED on Create Tasks; adjust it on
           *  the task afterwards. */}
        </>
      ) : (
        // Subtask / sub-subtask: compact single-line input + remove
        <div className="flex items-center gap-2">
          <span
            className={
              'h-1.5 w-1.5 rounded-full shrink-0 ' +
              (depth === 1
                ? 'bg-[color:var(--color-status-progress)]'
                : 'bg-[color:var(--color-ink-subtle)]')
            }
          />
          <input
            value={node.name}
            onChange={(e) => onUpdate(path, { name: e.target.value })}
            maxLength={100}
            placeholder={depth === 1 ? 'Subtask name' : 'Sub-subtask name'}
            className="flex-1 min-w-0 px-2 py-1 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-xs text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
          />
          <button
            type="button"
            onClick={() => onRemove(path)}
            title={depth === 1 ? 'Remove Subtask' : 'Remove Sub-subtask'}
            aria-label={depth === 1 ? 'Remove Subtask' : 'Remove Sub-subtask'}
            className="p-0.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
          >
            <X size={12} />
          </button>
        </div>
      )}
      {/* Children — render recursively. Each child increments depth
       *  and appends its own index to the path so updates address
       *  it precisely. */}
      {(node.subtasks ?? []).length > 0 ? (
        <div
          className={
            isTask
              ? 'pl-3 border-l-2 border-[color:var(--color-rule-strong,_#e2e8f0)] space-y-1.5'
              : 'space-y-1.5'
          }
        >
          {(node.subtasks ?? []).map((child, i) => (
            <NodeEditor
              key={i}
              node={child}
              path={[...path, i]}
              depth={depth + 1}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      ) : null}
      {/* "Add subtask" / "Add sub-subtask" affordance — only when
       *  nesting another level would stay within MAX_DEPTH. */}
      {depth < MAX_DEPTH ? (
        <button
          type="button"
          onClick={() => onAddChild(path)}
          className={
            'inline-flex items-center gap-1 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer ' +
            (isTask ? 'text-sm pl-3' : 'text-xs')
          }
        >
          <Plus size={11} />
          {depth === 0 ? 'Add Subtask' : 'Add Sub-subtask'}
        </button>
      ) : null}
    </div>
  )
}

export function SopPreviewDialog({
  open,
  listId,
  preview,
  onClose,
  onApproved,
  // Optional override for the "Approve" mutation. When provided, the
  // dialog calls this with the cleaned task tree INSTEAD of the
  // default `POST /lists/:listId/sops/:sopId/approve`. The library
  // SOP flow uses this because library SOPs aren't local rows (they
  // live in SOP Galaxy) and have no `sopId` the backend recognises —
  // it routes the create through the regular tasks endpoint instead.
  // The callback must resolve to `{ message: string, ... }` so the
  // success toast still has something to say.
  customApprove,
}) {
  const qc = useQueryClient()
  const [tasks, setTasks] = useState([])
  // Slice 2 version controls apply only to a STORED SOP (the upload path
  // has a sopId + a persisted blueprint). Library SOPs use customApprove
  // (no sopId, no blueprint to version).
  const isStoredSop =
    Boolean(preview?.sopId) && typeof customApprove !== 'function'
  const [blueprint, setBlueprint] = useState(null)

  useEffect(() => {
    if (open && preview) setTasks(preview.tasks)
  }, [open, preview])

  // Load the saved blueprint (its version list) when the dialog opens.
  useEffect(() => {
    if (!open || !isStoredSop) {
      setBlueprint(null)
      return undefined
    }
    let cancelled = false
    api
      .get(`/lists/${listId}/sops/${preview.sopId}/blueprint`)
      .then((res) => {
        if (!cancelled) {
          setBlueprint(sopBlueprintResponse.parse(res.data).blueprint)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, isStoredSop, listId, preview?.sopId])

  // Single set of immutable handlers passed down to every NodeEditor
  // — no per-depth specialised handlers, so adding deeper levels later
  // is just bumping MAX_DEPTH (and tweaking the indent style table).
  const onUpdate = (path, patch) =>
    setTasks((prev) => updateAtPath(prev, path, patch))
  const onRemove = (path) => setTasks((prev) => removeAtPath(prev, path))
  const onAddChild = (path) =>
    setTasks((prev) => addChildAtPath(prev, path, /* isTask */ path.length === 0))

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!listId || !preview) throw new Error('Missing context.')
      const cleaned = cleanTree(tasks)
      if (cleaned.length === 0) throw new Error('Add at least one task before approving.')
      // Library SOPs route through `customApprove` because they have
      // no local `sopId` — the regular `/sops/:sopId/approve` endpoint
      // would 404. The override returns the same `{ message, ... }`
      // shape so the success toast stays consistent.
      if (typeof customApprove === 'function') {
        return await customApprove(cleaned)
      }
      const res = await api.post(
        `/lists/${listId}/sops/${preview.sopId}/approve`,
        { tasks: cleaned },
      )
      return approveSopResponse.parse(res.data)
    },
    onSuccess: (result) => {
      toast.success(result.message)
      void qc.invalidateQueries({ queryKey: ['tasks', listId] })
      void qc.invalidateQueries({ queryKey: ['sops', listId] })
      // SOP approval creates tasks → refresh the Space Overview counts.
      void qc.invalidateQueries({ queryKey: ['space-overview'] })
      // Distinguish approval from a plain cancel/close — callers like
      // the Create Task page need to know whether tasks were actually
      // created so they can decide whether to navigate away or stay.
      if (onApproved) onApproved(result)
      else onClose()
    },
    onError: (err) => {
      // A plan-cap hit is already announced by the API response
      // interceptor as a single amber "upgrade your plan" warning. Without
      // this guard we add a second, red toast carrying the SAME sentence,
      // so the user sees the identical message twice in two styles.
      if (isPlanLimitError(err)) return
      // Dead session: the interceptor already force-logged-out and toasted.
      if (isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  // Validate BEFORE hitting the API so a blank task name shows a clear message
  // and blocks submission — instead of being silently dropped and surfacing as
  // a generic server error. Runs off the same tree the mutation cleans.
  function handleCreate() {
    const nameError = firstNameError(tasks)
    if (nameError) {
      toast.error(nameError)
      return
    }
    if (cleanTree(tasks).length === 0) {
      toast.error('Add at least one task before approving.')
      return
    }
    approveMutation.mutate()
  }

  const refreshBlueprint = () =>
    api
      .get(`/lists/${listId}/sops/${preview?.sopId}/blueprint`)
      .then((res) => setBlueprint(sopBlueprintResponse.parse(res.data).blueprint))
      .catch(() => {})

  // Regenerate with AI → a NEW version (the old ones are kept).
  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        `/lists/${listId}/sops/${preview.sopId}/generate-tasks`,
        { regenerate: true },
        // Regenerate re-runs the AI (15–90s) — override the 15s default
        // timeout so it doesn't surface as "Can't reach the server".
        { timeout: 120_000 },
      )
      return generateSopResponse.parse(res.data)
    },
    onSuccess: (result) => {
      setTasks(result.tasks)
      void refreshBlueprint()
      toast.success('Regenerated with AI — saved as a new version.')
    },
    onError: (err) => {
      // A plan-cap hit is already announced by the API response
      // interceptor as a single amber "upgrade your plan" warning. Without
      // this guard we add a second, red toast carrying the SAME sentence,
      // so the user sees the identical message twice in two styles.
      if (isPlanLimitError(err)) return
      // Dead session: the interceptor already force-logged-out and toasted.
      if (isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  // Initial AI generation for a freshly-uploaded SOP whose upload deferred it
  // (decoupled so the upload request never exceeds the proxy timeout). Same
  // endpoint as Regenerate but WITHOUT `regenerate: true`, so a saved Task
  // Blueprint (content-hash match) is reused when present — preserving the
  // existing generation behaviour. Runs once automatically on open (below).
  const initialGenerateMutation = useMutation({
    mutationFn: async () => {
      // Two decoupled sources: a stored SOP (has sopId) hits the per-SOP
      // endpoint; a LIBRARY SOP (SOP Galaxy — no local sopId) posts its
      // flattened content to the library endpoint. Both keep the request short
      // enough to clear the gateway timeout and drive the same loading UI.
      if (preview?.library) {
        const res = await api.post(
          `/lists/${listId}/sops/library/generate-tasks`,
          {
            title: preview.sopTitle,
            content: preview.libraryContent,
            // "From SOP" forces a fresh AI read, bypassing the saved blueprint.
            ...(preview.readFresh ? { regenerate: true } : {}),
          },
          { timeout: 120_000 },
        )
        // Library response has sopId:null, so skip the strict schema parse.
        return { tasks: Array.isArray(res.data?.tasks) ? res.data.tasks : [] }
      }
      const res = await api.post(
        `/lists/${listId}/sops/${preview.sopId}/generate-tasks`,
        // "From SOP" (readFresh) forces a fresh AI read of the SOP, bypassing
        // the saved Task Blueprint. The upload flow leaves it off so a
        // first-generation cache hit (if any) is still honoured.
        preview.readFresh ? { regenerate: true } : {},
        // AI can take 15–90s — override the 15s default so a slow (but valid)
        // generation isn't cut off client-side as "Can't reach the server".
        { timeout: 120_000 },
      )
      return generateSopResponse.parse(res.data)
    },
    onSuccess: (result) => {
      setTasks(result.tasks)
      // Library SOPs have no per-sopId blueprint to refresh.
      if (preview?.sopId) void refreshBlueprint()
    },
    onError: (err) => {
      // Mirror the Regenerate guards — the interceptor already toasts plan-cap
      // + dead-session; anything else gets one clean, actionable toast (the
      // empty state below also offers a Try-again button).
      if (isPlanLimitError(err)) return
      if (isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  // Kick off that first generation exactly once when the dialog opens in a
  // pending-generation state (an upload that returned the saved SOP without
  // running AI). The ref guard prevents a re-fire on unrelated re-renders.
  const autoGenStartedRef = useRef(false)
  useEffect(() => {
    if (!open) {
      autoGenStartedRef.current = false
      return
    }
    // `pendingGeneration` is the intent signal (set only by the decoupled
    // upload / SOPs-Library / library-SOP flows). A stored SOP generates via
    // its sopId; a library SOP via its flattened content — either is enough.
    if (
      preview?.pendingGeneration &&
      (preview?.sopId || preview?.libraryContent) &&
      !autoGenStartedRef.current
    ) {
      autoGenStartedRef.current = true
      initialGenerateMutation.mutate()
    }
    // initialGenerateMutation is stable for the dialog's lifetime; the ref
    // guard makes this safe to run on the open/preview transition only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preview])

  // Switch which saved version is shown (and what Create Tasks will use).
  const switchVersionMutation = useMutation({
    mutationFn: async (version) => {
      const res = await api.post(
        `/lists/${listId}/sops/${preview.sopId}/blueprint/version`,
        { version },
      )
      return sopBlueprintResponse.parse(res.data).blueprint
    },
    onSuccess: (bp) => {
      if (!bp) return
      setBlueprint(bp)
      setTasks(bp.tasks)
    },
    onError: (err) => {
      // A plan-cap hit is already announced by the API response
      // interceptor as a single amber "upgrade your plan" warning. Without
      // this guard we add a second, red toast carrying the SAME sentence,
      // so the user sees the identical message twice in two styles.
      if (isPlanLimitError(err)) return
      // Dead session: the interceptor already force-logged-out and toasted.
      if (isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  // Persist the current edits as a NEW manual version of the blueprint.
  const saveBlueprintMutation = useMutation({
    mutationFn: async () => {
      const cleaned = cleanTree(tasks)
      if (cleaned.length === 0) {
        throw new Error('Add at least one task before saving the blueprint.')
      }
      const res = await api.put(
        `/lists/${listId}/sops/${preview.sopId}/blueprint`,
        { tasks: cleaned },
      )
      return sopBlueprintResponse.parse(res.data).blueprint
    },
    onSuccess: (bp) => {
      if (bp) setBlueprint(bp)
      toast.success('Saved your edits as a new blueprint version.')
    },
    onError: (err) => {
      // A plan-cap hit is already announced by the API response
      // interceptor as a single amber "upgrade your plan" warning. Without
      // this guard we add a second, red toast carrying the SAME sentence,
      // so the user sees the identical message twice in two styles.
      if (isPlanLimitError(err)) return
      // Dead session: the interceptor already force-logged-out and toasted.
      if (isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const blueprintBusy =
    regenerateMutation.isPending ||
    switchVersionMutation.isPending ||
    saveBlueprintMutation.isPending

  // Version navigation — versions are appended 1..N; find the neighbours.
  const versionNums = (blueprint?.versions ?? [])
    .map((v) => v.version)
    .sort((a, b) => a - b)
  const curIdx = versionNums.indexOf(blueprint?.currentVersion)
  const prevVersion = curIdx > 0 ? versionNums[curIdx - 1] : null
  const nextVersion =
    curIdx >= 0 && curIdx < versionNums.length - 1
      ? versionNums[curIdx + 1]
      : null

  const totalTasks = tasks.length
  // Now counts every subtask at any depth — the recursive walker
  // covers depth-1 (subtasks) AND depth-2 (sub-subtasks) so the
  // header summary reflects the full fan-out.
  const totalSubs = countDescendants(tasks)

  // True only while the initial AI generation is ACTIVELY running (not after it
  // succeeds or fails) — used to hide the manual "Add task" affordance so it
  // isn't offered mid-generation.
  const isGenerating =
    initialGenerateMutation.isPending ||
    (preview?.pendingGeneration &&
      !initialGenerateMutation.isSuccess &&
      !initialGenerateMutation.isError)

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Honest header per source: a saved blueprint, a library SOP, or a
      // fresh AI draft from an upload.
      title={
        preview?.source === 'blueprint'
          ? 'Review Tasks from Blueprint'
          : customApprove
            ? 'Review Tasks from SOP'
            : 'Review AI-Generated Tasks'
      }
      description={
        isGenerating
          ? // No task count while the AI is still drafting (it would read
            // "creates 0 tasks"). Keep the source, promise the count later.
            `Drafting tasks from "${preview?.sopTitle ?? 'your SOP'}"…`
          : preview
            ? `${preview.source === 'blueprint' ? 'Reused from saved blueprint' : 'Drafted from'} "${preview.sopTitle}". Edit anything below — approving creates ${totalTasks} task${totalTasks === 1 ? '' : 's'}` +
              (totalSubs > 0
                ? ` and ${totalSubs} subtask${totalSubs === 1 ? '' : 's'} in TO DO.`
                : ' in TO DO.')
            : ''
      }
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={approveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            loading={approveMutation.isPending}
            disabled={tasks.length === 0}
          >
            Create Tasks
          </Button>
        </>
      }
    >
      <div className="space-y-3 max-h-[60vh] overflow-auto pr-1">
        {/* Slice 3b — the SOP changed since this blueprint was generated, so
         *  it's reused as-is but flagged; the user can Regenerate to refresh. */}
        {preview?.provenance?.outdated ? (
          <div className="flex items-start gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-xs text-[color:var(--color-ink)]">
            <AlertTriangle
              size={14}
              className="shrink-0 mt-0.5"
              style={{ color: '#d97706' }}
            />
            <span>
              This SOP changed since the blueprint was generated, so it may be{' '}
              <strong>outdated</strong>. Use it as-is, or{' '}
              <strong>Regenerate with AI</strong> to refresh it.
            </span>
          </div>
        ) : null}
        {/* Task Blueprint reuse — when the breakdown came from a saved
         *  blueprint, no AI ran (no credits spent). Reassures the user the
         *  repeat generation was instant + free. */}
        {preview?.provenance?.source === 'cache' ? (
          <div className="flex items-center gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-xs text-[color:var(--color-ink-muted)]">
            <span
              className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-status-complete)] shrink-0"
              aria-hidden="true"
            />
            Reused a saved breakdown for this SOP — no AI was used.
          </div>
        ) : null}
        {/* Blueprint version controls (stored SOPs only) — switch version,
         *  regenerate (new version), or save edits as a new version. */}
        {isStoredSop && blueprint ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2.5">
            {/* Plain-language explainer so "blueprint" isn't jargon: this
             *  breakdown is saved and reused next time with no AI cost. */}
            <div className="flex items-start gap-2 min-w-0">
              <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-md bg-[color:var(--color-card)] text-[color:var(--color-primary)] shrink-0">
                <Sparkles size={13} />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[color:var(--color-ink)]">
                  Saved as a reusable blueprint
                </p>
                <p className="text-[11px] text-[color:var(--color-ink-muted)]">
                  {blueprint.versions.length > 1
                    ? `Version ${blueprint.currentVersion} of ${blueprint.versions.length} · reused next time with no AI cost.`
                    : 'This breakdown is reused next time — no AI needed.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {blueprint.versions.length > 1 ? (
                <div className="flex items-center gap-0.5 mr-1">
                  <button
                    type="button"
                    onClick={() =>
                      prevVersion != null &&
                      switchVersionMutation.mutate(prevVersion)
                    }
                    disabled={prevVersion == null || blueprintBusy}
                    title="Previous version"
                    aria-label="Previous version"
                    className="p-1 rounded hover:bg-[color:var(--color-card-hover)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      nextVersion != null &&
                      switchVersionMutation.mutate(nextVersion)
                    }
                    disabled={nextVersion == null || blueprintBusy}
                    title="Next version"
                    aria-label="Next version"
                    className="p-1 rounded hover:bg-[color:var(--color-card-hover)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => saveBlueprintMutation.mutate()}
                disabled={blueprintBusy || tasks.length === 0}
                title="Save your edits as a new version of this blueprint"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[color:var(--color-rule)] text-xs font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <Save size={13} />
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => regenerateMutation.mutate()}
                disabled={blueprintBusy}
                title="Ask the AI for a fresh breakdown (saved as a new version)"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[color:var(--color-rule)] text-xs font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <RefreshCw
                  size={13}
                  className={regenerateMutation.isPending ? 'animate-spin' : ''}
                />
                Regenerate
              </button>
            </div>
          </div>
        ) : null}
        {tasks.length === 0 ? (
          initialGenerateMutation.isError ? (
            // The initial AI generation failed / timed out — the SOP IS saved,
            // so offer a clean retry (same call) instead of a dead end. Never a
            // "server error": the upload itself succeeded.
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm text-[color:var(--color-ink)]">
              <AlertTriangle size={14} className="shrink-0" style={{ color: '#d97706' }} />
              <span>AI couldn&apos;t draft tasks just now.</span>
              <button
                type="button"
                onClick={() => initialGenerateMutation.mutate()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[color:var(--color-rule)] text-xs font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                <RefreshCw size={13} />
                Try Again
              </button>
              <span className="text-[color:var(--color-ink-muted)]">or add a task manually below.</span>
            </div>
          ) : isGenerating ? (
            // Generation is running as its own request — show a live, centred
            // state (not a false error) while the AI drafts tasks.
            <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-6 py-12 text-center">
              {/* Spinner ring orbiting a soft-tinted AI mark. */}
              <div className="relative flex h-14 w-14 items-center justify-center">
                <span className="absolute inset-0 rounded-full border-2 border-[color:var(--color-rule)]" />
                <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-[color:var(--color-primary)] animate-spin" />
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--color-card)]">
                  <Sparkles size={18} className="text-[color:var(--color-primary)]" />
                </span>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-[color:var(--color-ink)]">
                  Generating tasks with AI
                </p>
                <p className="text-xs text-[color:var(--color-ink-muted)]">
                  Drafting tasks and subtasks from your SOP — this can take up to a minute.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[color:var(--color-ink-muted)] italic">
              No tasks. Click "Add task" to start.
            </p>
          )
        ) : (
          tasks.map((t, ti) => (
            <NodeEditor
              key={ti}
              node={t}
              path={[ti]}
              depth={0}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onAddChild={onAddChild}
            />
          ))
        )}
        {/* Hidden while the AI is generating — no point offering a manual add
         *  before the drafted tasks arrive. Returns after success/failure. */}
        {!isGenerating ? (
          <button
            type="button"
            onClick={() => onAddChild([])}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-dashed border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:border-[color:var(--color-accent)] cursor-pointer"
          >
            <Plus size={14} />
            Add Task
          </button>
        ) : null}
      </div>
    </Modal>
  )
}
